import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { api } from '../lib/api/client'
import { useSession } from '../lib/auth'
import { Bouton, Champ, Saisie } from '../components/ui/Base'
import { Bandeau, MessageErreur } from '../components/ui/Etats'
import { useToast } from '../components/ui/Toasts'

/** Politique du §8.2 : 12 caractères minimum, pas d'expiration forcée. */
const LONGUEUR_MINIMALE = 12

const motDePasseFort = z
  .string()
  .min(LONGUEUR_MINIMALE, `Le mot de passe doit comporter au moins ${LONGUEUR_MINIMALE} caractères.`)

/** Indicateur de force — indicatif, la validation reste côté serveur. */
function force(motDePasse: string): { note: number; libelle: string } {
  let note = 0
  if (motDePasse.length >= LONGUEUR_MINIMALE) note++
  if (motDePasse.length >= 16) note++
  if (/[a-z]/.test(motDePasse) && /[A-Z]/.test(motDePasse)) note++
  if (/\d/.test(motDePasse)) note++
  if (/[^\w\s]/.test(motDePasse)) note++
  const libelles = ['Très faible', 'Faible', 'Moyen', 'Correct', 'Bon', 'Excellent']
  return { note, libelle: libelles[note] ?? 'Très faible' }
}

function IndicateurForce({ valeur }: { valeur: string }) {
  const { note, libelle } = force(valeur)
  if (!valeur) return null
  return (
    <div className="space-y-1">
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded ${
              i < note ? (note <= 2 ? 'bg-red-400' : note <= 3 ? 'bg-amber-400' : 'bg-emerald-500') : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
      <p className="text-xs text-slate-500" aria-live="polite">
        Force du mot de passe : {libelle}
      </p>
    </div>
  )
}

/* ── Mot de passe oublié ──────────────────────────────────────────────── */

const schemaOubli = z.object({
  email: z.string().min(1, 'Renseignez votre adresse.').email('Adresse électronique invalide.'),
})

export function MotDePasseOubli() {
  const [envoye, setEnvoye] = useState(false)
  const { register, handleSubmit, formState } = useForm<z.infer<typeof schemaOubli>>({
    resolver: zodResolver(schemaOubli),
  })

  async function soumettre(valeurs: z.infer<typeof schemaOubli>) {
    // La route répond 204 quoi qu'il arrive (§8.6). L'interface affiche donc
    // **le même message** que le compte existe ou non : trahir la différence
    // ici annulerait la précaution prise côté serveur.
    try {
      await api.post('/auth/mot-de-passe/oubli', { email: valeurs.email })
    } catch {
      /* même en cas d'échec réseau, on n'en dit pas plus */
    }
    setEnvoye(true)
  }

  return (
    <CadreAuth titre="Mot de passe oublié">
      {envoye ? (
        <Bandeau ton="info">
          Si un compte existe pour cette adresse, un lien de réinitialisation vient d&apos;être
          envoyé. Vérifiez votre messagerie.
        </Bandeau>
      ) : (
        <form onSubmit={handleSubmit(soumettre)} className="space-y-4" noValidate>
          <Champ libelle="Adresse électronique" id="email" erreur={formState.errors.email?.message} obligatoire>
            <Saisie id="email" type="email" autoFocus {...register('email')} />
          </Champ>
          <Bouton type="submit" className="w-full" disabled={formState.isSubmitting}>
            Envoyer le lien
          </Bouton>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to="/connexion" className="text-sky-700 hover:underline">
          Retour à la connexion
        </Link>
      </p>
    </CadreAuth>
  )
}

/* ── Réinitialisation ─────────────────────────────────────────────────── */

const schemaReinit = z
  .object({
    nouveau_mot_de_passe: motDePasseFort,
    confirmation: z.string(),
  })
  .refine((v) => v.nouveau_mot_de_passe === v.confirmation, {
    message: 'Les deux mots de passe diffèrent.',
    path: ['confirmation'],
  })

export function MotDePasseReinitialiser() {
  const [parametres] = useSearchParams()
  const naviguer = useNavigate()
  const toast = useToast()
  const [erreur, setErreur] = useState<unknown>(null)
  const jeton = parametres.get('jeton') ?? ''

  const { register, handleSubmit, watch, formState } = useForm<z.infer<typeof schemaReinit>>({
    resolver: zodResolver(schemaReinit),
  })

  async function soumettre(valeurs: z.infer<typeof schemaReinit>) {
    setErreur(null)
    try {
      await api.post('/auth/mot-de-passe/reinitialiser', {
        jeton,
        nouveau_mot_de_passe: valeurs.nouveau_mot_de_passe,
      })
      toast('Mot de passe réinitialisé. Vous pouvez vous connecter.', 'succes')
      naviguer('/connexion')
    } catch (e) {
      setErreur(e)
    }
  }

  return (
    <CadreAuth titre="Nouveau mot de passe">
      {!jeton ? (
        <Bandeau ton="attention">
          Ce lien est incomplet. Demandez-en un nouveau depuis « Mot de passe oublié ».
        </Bandeau>
      ) : (
        <form onSubmit={handleSubmit(soumettre)} className="space-y-4" noValidate>
          {erreur ? <MessageErreur erreur={erreur} /> : null}
          <Champ
            libelle="Nouveau mot de passe"
            id="nouveau"
            erreur={formState.errors.nouveau_mot_de_passe?.message}
            aide={`${LONGUEUR_MINIMALE} caractères minimum.`}
            obligatoire
          >
            <Saisie id="nouveau" type="password" autoComplete="new-password" {...register('nouveau_mot_de_passe')} />
          </Champ>
          <IndicateurForce valeur={watch('nouveau_mot_de_passe') ?? ''} />
          <Champ libelle="Confirmation" id="confirmation" erreur={formState.errors.confirmation?.message} obligatoire>
            <Saisie id="confirmation" type="password" autoComplete="new-password" {...register('confirmation')} />
          </Champ>
          <Bouton type="submit" className="w-full" disabled={formState.isSubmitting}>
            Enregistrer
          </Bouton>
        </form>
      )}
    </CadreAuth>
  )
}

/* ── Changement volontaire ────────────────────────────────────────────── */

const schemaChangement = z
  .object({
    ancien: z.string().min(1, 'Renseignez votre mot de passe actuel.'),
    nouveau: motDePasseFort,
    confirmation: z.string(),
  })
  .refine((v) => v.nouveau === v.confirmation, {
    message: 'Les deux mots de passe diffèrent.',
    path: ['confirmation'],
  })

export function MotDePasseChanger() {
  const { utilisateur, rafraichirProfil } = useSession()
  const naviguer = useNavigate()
  const toast = useToast()
  const [erreur, setErreur] = useState<unknown>(null)

  const { register, handleSubmit, watch, formState } = useForm<z.infer<typeof schemaChangement>>({
    resolver: zodResolver(schemaChangement),
  })

  async function soumettre(valeurs: z.infer<typeof schemaChangement>) {
    setErreur(null)
    try {
      await api.post('/auth/mot-de-passe/changer', {
        ancien: valeurs.ancien,
        nouveau: valeurs.nouveau,
      })
      await rafraichirProfil()
      toast('Mot de passe modifié.', 'succes')
      naviguer('/planning')
    } catch (e) {
      setErreur(e)
    }
  }

  return (
    <CadreAuth titre="Changer de mot de passe">
      {utilisateur?.doit_changer_mot_de_passe ? (
        <div className="mb-4">
          <Bandeau ton="attention">
            Votre mot de passe doit être changé avant d&apos;accéder à l&apos;application.
          </Bandeau>
        </div>
      ) : null}

      <form onSubmit={handleSubmit(soumettre)} className="space-y-4" noValidate>
        {erreur ? <MessageErreur erreur={erreur} /> : null}
        <Champ libelle="Mot de passe actuel" id="ancien" erreur={formState.errors.ancien?.message} obligatoire>
          <Saisie id="ancien" type="password" autoComplete="current-password" autoFocus {...register('ancien')} />
        </Champ>
        <Champ
          libelle="Nouveau mot de passe"
          id="nouveau"
          erreur={formState.errors.nouveau?.message}
          aide={`${LONGUEUR_MINIMALE} caractères minimum.`}
          obligatoire
        >
          <Saisie id="nouveau" type="password" autoComplete="new-password" {...register('nouveau')} />
        </Champ>
        <IndicateurForce valeur={watch('nouveau') ?? ''} />
        <Champ libelle="Confirmation" id="confirmation" erreur={formState.errors.confirmation?.message} obligatoire>
          <Saisie id="confirmation" type="password" autoComplete="new-password" {...register('confirmation')} />
        </Champ>
        <Bouton type="submit" className="w-full" disabled={formState.isSubmitting}>
          Enregistrer
        </Bouton>
      </form>
    </CadreAuth>
  )
}

function CadreAuth({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-6 text-center text-xl font-semibold text-slate-900">{titre}</h1>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
      </div>
    </div>
  )
}
